import { useState } from "react"
import { Link, usePage, router } from "@inertiajs/react"
import { useTheme } from "next-themes"
import {
  IconHome,
  IconBluetooth,
  IconUserCircle,
  IconCreditCard,
  IconLogout,
  IconMoon,
} from "@tabler/icons-react"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Switch } from "@/components/ui/switch"
import { Separator } from "@/components/ui/separator"

const navItems = [
  { title: "Home", url: "/", icon: IconHome },
  { title: "Devices", url: "/devices", icon: IconBluetooth },
]

export function BottomNav() {
  const { url, props } = usePage<{ auth: { user: { id: number; email_address: string } | null } }>()
  const user = props.auth?.user
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { resolvedTheme, setTheme } = useTheme()
  const isDark = resolvedTheme === "dark"

  const isActive = (itemUrl: string) => {
    if (itemUrl === "/") return url === "/"
    return url === itemUrl || url.startsWith(`${itemUrl}/`)
  }

  const handleLogout = () => {
    setDrawerOpen(false)
    router.delete("/session", { preserveScroll: true })
  }

  const toggleDarkMode = (checked: boolean) => {
    setTheme(checked ? "dark" : "light")
  }

  const getInitials = (email: string) => email.substring(0, 2).toUpperCase()

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background pb-[env(safe-area-inset-bottom)] md:hidden">
      <div className="flex h-16 items-stretch justify-around">
        {navItems.map((item) => {
          const active = isActive(item.url)
          return (
            <Link
              key={item.title}
              href={item.url}
              className={`flex flex-1 flex-col items-center justify-center gap-1 min-h-11 text-xs transition-colors ${
                active
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <item.icon className="size-5" strokeWidth={active ? 2.5 : 2} />
              <span className={active ? "font-medium" : ""}>{item.title}</span>
            </Link>
          )
        })}

        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
          <DrawerTrigger asChild>
            <button
              className="flex flex-1 flex-col items-center justify-center gap-1 min-h-11 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <IconUserCircle className="size-5" strokeWidth={2} />
              <span>Account</span>
            </button>
          </DrawerTrigger>
          <DrawerContent>
            <DrawerHeader className="text-left">
              <div className="flex items-center gap-3">
                {user && (
                  <Avatar className="h-10 w-10 rounded-lg">
                    <AvatarFallback className="rounded-lg">
                      {getInitials(user.email_address)}
                    </AvatarFallback>
                  </Avatar>
                )}
                <div className="grid text-left text-sm leading-tight">
                  <DrawerTitle className="font-medium">{user?.email_address}</DrawerTitle>
                  <span className="text-muted-foreground text-xs">Logged in</span>
                </div>
              </div>
            </DrawerHeader>
            <div className="px-4 pb-6">
              <Separator className="mb-2" />
              <div className="flex flex-col">
                <Link
                  href="/billing"
                  onClick={() => setDrawerOpen(false)}
                  className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm hover:bg-muted transition-colors"
                >
                  <IconCreditCard className="size-4" />
                  Billing
                </Link>
                <div className="flex items-center justify-between rounded-md px-3 py-2.5 text-sm">
                  <div className="flex items-center gap-3">
                    <IconMoon className="size-4" />
                    Dark Mode
                  </div>
                  <Switch checked={isDark} onCheckedChange={toggleDarkMode} />
                </div>
                <Separator className="my-2" />
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-destructive hover:bg-muted transition-colors"
                >
                  <IconLogout className="size-4" />
                  Log out
                </button>
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      </div>
    </nav>
  )
}
